import { useState } from 'react'
import { FiKey, FiX } from 'react-icons/fi'
import toast from 'react-hot-toast'
import { API_URL } from '../../config/api'
import { Btn } from '../../ui'

/*
 * ModalCambiarPinV2 — DS v2.
 * Usuario asigna o cambia su propio PIN. Replica la lógica del modal legacy
 * (PUT /empleados/cambiar-pin). Si tienePin=true pide PIN actual.
 */

const ModalCambiarPinV2 = ({ tienePin, onClose, onSuccess }) => {
  const [form, setForm] = useState({ pinActual: '', pinNuevo: '', pinConfirmar: '' })
  const [guardando, setGuardando] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (form.pinNuevo !== form.pinConfirmar) {
      toast.error('Los PINs no coinciden')
      return
    }
    if (form.pinNuevo.length < 5 || form.pinNuevo.length > 8 || !/^\d+$/.test(form.pinNuevo)) {
      toast.error('El PIN debe tener entre 5 y 8 dígitos')
      return
    }

    setGuardando(true)
    try {
      const token = localStorage.getItem('token')
      const body = { pinNuevo: form.pinNuevo }
      if (tienePin) body.pinActual = form.pinActual

      const response = await fetch(`${API_URL}/empleados/cambiar-pin`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body)
      })

      if (response.ok) {
        toast.success(tienePin ? 'PIN cambiado correctamente' : 'PIN asignado correctamente')
        onSuccess?.()
        onClose()
      } else {
        const error = await response.json()
        toast.error(error.error || 'Error al cambiar PIN')
      }
    } catch {
      toast.error('Error al cambiar PIN')
    } finally {
      setGuardando(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="ui-card w-full max-w-md">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-[var(--line)]">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-soft bg-[rgba(124,58,237,.10)] flex items-center justify-center">
              <FiKey size={15} className="text-[#7C3AED]" />
            </div>
            <div>
              <div className="eyebrow mb-1">Acceso rápido</div>
              <h3 className="font-display text-[20px] text-[var(--ink)] leading-tight">
                {tienePin ? 'Cambiar PIN.' : 'Asignar PIN.'}
              </h3>
              <p className="text-[12px] text-[var(--ink-3)] mt-0.5">
                {tienePin
                  ? 'Cambia tu PIN actual por uno nuevo de 5 a 8 dígitos.'
                  : 'Crea un PIN de 5 a 8 dígitos para fichar rápido sin escribir email.'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-soft text-[var(--ink-3)] hover:text-[var(--ink)] hover:bg-[var(--bg-2)] transition-colors"
            aria-label="Cerrar"
            disabled={guardando}
          >
            <FiX size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {tienePin && (
            <div>
              <label className="eyebrow block mb-1.5">PIN actual *</label>
              <input
                type="password"
                inputMode="numeric"
                pattern="\d*"
                value={form.pinActual}
                onChange={e => setForm({ ...form, pinActual: e.target.value.replace(/\D/g, '') })}
                className="w-full px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft font-mono tnum text-center text-[18px] tracking-widest focus:outline-none focus:border-[var(--ink-3)]"
                required
                maxLength={8}
                autoFocus
              />
            </div>
          )}

          <div>
            <label className="eyebrow block mb-1.5">Nuevo PIN *</label>
            <input
              type="password"
              inputMode="numeric"
              pattern="\d*"
              value={form.pinNuevo}
              onChange={e => setForm({ ...form, pinNuevo: e.target.value.replace(/\D/g, '') })}
              className="w-full px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft font-mono tnum text-center text-[18px] tracking-widest focus:outline-none focus:border-[var(--ink-3)]"
              required
              minLength={5}
              maxLength={8}
              placeholder="5 a 8 dígitos"
              autoFocus={!tienePin}
            />
          </div>

          <div>
            <label className="eyebrow block mb-1.5">Confirmar nuevo PIN *</label>
            <input
              type="password"
              inputMode="numeric"
              pattern="\d*"
              value={form.pinConfirmar}
              onChange={e => setForm({ ...form, pinConfirmar: e.target.value.replace(/\D/g, '') })}
              className={`w-full px-3 py-2 bg-[var(--bg-2)] border rounded-soft font-mono tnum text-center text-[18px] tracking-widest focus:outline-none ${
                form.pinConfirmar && form.pinNuevo !== form.pinConfirmar
                  ? 'border-[#C13030] focus:border-[#C13030]'
                  : 'border-[var(--line)] focus:border-[var(--ink-3)]'
              }`}
              required
              minLength={5}
              maxLength={8}
            />
            {form.pinConfirmar && form.pinNuevo !== form.pinConfirmar && (
              <p className="text-[11px] text-[#C13030] mt-1">Los PINs no coinciden</p>
            )}
          </div>

          <div className="flex gap-3 pt-1">
            <Btn type="button" variant="ghost" className="flex-1" onClick={onClose} disabled={guardando}>
              Cancelar
            </Btn>
            <Btn type="submit" variant="primary" className="flex-1" disabled={guardando}>
              {guardando ? 'Guardando…' : tienePin ? 'Cambiar PIN' : 'Asignar PIN'}
            </Btn>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ModalCambiarPinV2
